import { useLocale, useTranslations } from "next-intl";
import { formatPriceDisplay } from "@lms/backend/lib/courses/price-format";
import { readApiErrorResponse } from "@/lib/api-error";
import { apiFetch } from "@/services/http/api-fetch";

type Price = { currency: string; amount: number; isActive: boolean };

export type PublishOutcome =
  | { status: "published"; course: Record<string, unknown> }
  | { status: "cancelled" }
  | { status: "failed"; error: unknown };

/** "₹4,999.50 · $49.99", INR first — what students will be asked to pay. */
export function describePrices(prices: Price[], locale?: string): string | null {
  const live = prices
    .filter((p) => p.isActive && p.amount > 0)
    .sort((a, b) => (a.currency === "INR" ? -1 : b.currency === "INR" ? 1 : 0));
  if (live.length === 0) return null;
  return live.map((p) => formatPriceDisplay(p.currency, p.amount, locale)).join(" · ");
}

/**
 * CRS-5: Publish asks first, naming the price students will pay — or, with no price, asking
 * the instructor to confirm the course is free (the API refuses a priceless publish without
 * `confirmFree`, and one with no lessons at all). R3-L12: when no session can be completed yet
 * it asks once more and sends `confirmNoCompletableLessons`; R-3: when a session's video is still
 * uploading, encoding or failed, `confirmLessonsAwaitVideo`. Shared by the builder and the list.
 */
export function usePublishCourse(confirm: (message: string) => boolean = (m) => window.confirm(m)) {
  const t = useTranslations("instructor");
  const locale = useLocale();

  return async function publish(courseId: string): Promise<PublishOutcome> {
    try {
      const pricesRes = await apiFetch(`/v1/instructor/courses/${courseId}/prices`);
      if (!pricesRes.ok) return { status: "failed", error: await readApiErrorResponse(pricesRes) };
      const { data } = (await pricesRes.json()) as { data: { prices: Price[] } };
      const described = describePrices(data.prices, locale);

      const ok = confirm(
        described ? t("publishConfirmPrice", { prices: described }) : t("publishConfirmFree"),
      );
      if (!ok) return { status: "cancelled" };

      const body: Record<string, boolean> = described ? {} : { confirmFree: true };
      const send = () =>
        apiFetch(`/v1/instructor/courses/${courseId}/publish`, {
          method: "POST",
          body: JSON.stringify(body),
        });
      // R3-L12 / R-3: confirmations the API asks for — no session can be completed yet, or some
      // sessions' videos are still on their way (they hold students' completion). Each is asked
      // once, then the publish is sent again with its flag.
      const confirmations: Record<string, { flag: string; message: string }> = {
        PUBLISH_NO_COMPLETABLE_LESSONS: {
          flag: "confirmNoCompletableLessons",
          message: t("publishConfirmNoCompletable"),
        },
        PUBLISH_LESSONS_AWAIT_VIDEO: {
          flag: "confirmLessonsAwaitVideo",
          message: t("publishConfirmLessonsAwaitVideo"),
        },
      };
      let res = await send();
      while (!res.ok) {
        const error = await readApiErrorResponse(res);
        const ask = error.code ? confirmations[error.code] : undefined;
        if (!ask || body[ask.flag]) return { status: "failed", error };
        if (!confirm(ask.message)) return { status: "cancelled" };
        body[ask.flag] = true;
        res = await send();
      }
      const json = (await res.json()) as { data: { course: Record<string, unknown> } };
      return { status: "published", course: json.data.course };
    } catch (error) {
      return { status: "failed", error };
    }
  };
}
